import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Form, Input, InputNumber, Button, Card, Typography, message, Select, Spin } from 'antd'
import { getProduct, updateProduct } from '../../services/product'
import { getCurrencies } from '../../services/config'
import type { CurrencyOption } from '../../services/config'
import type { Product } from '../../types'

const { Title } = Typography
const { TextArea } = Input

export default function ProductEdit() {
  const { id } = useParams<{ id: string }>()
  const [product, setProduct] = useState<Product | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [currencyOptions, setCurrencyOptions] = useState<CurrencyOption[]>([])
  const [form] = Form.useForm()

  const navigate = useNavigate()

  useEffect(() => {
    getCurrencies()
      .then((res) => setCurrencyOptions(res.data.currencies))
      .catch(() => setCurrencyOptions([
        { code: 'USD', label: 'USD' },
        { code: 'CNY', label: 'CNY' },
        { code: 'HKD', label: 'HKD' }
      ]))
  }, [])

  useEffect(() => {
    if (id) {
      getProduct(id)
        .then((res) => {
          setProduct(res.data)
        const [name, description, price, currency] = [
          res.data.name,
          res.data.description,
          String(res.data.price),
          res.data.currency
        ]
        form.setFieldsValue({
            name,
            description,
            price,
            currency,
          })
        })
        .catch(() => message.error('產品不存在'))
        .finally(() => setLoading(false))
    }
  }, [id, form])

  const handleSubmit = async (values: { name: string; description?: string; price: string | number; currency: string }) => {
    if (!id) return
    setSaving(true)
    try {
      const request = {
        name: values.name,
        description: values.description,
        price: String(values.price),
        currency: values.currency
      }
      await updateProduct(id, request)
      message.success('產品更新成功')
      navigate('/dashboard/products')
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : '更新失敗')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />
  }

  if (!product) {
    return <div>產品不存在</div>
  }

  const ensureCurrentCurrencyOption = (options: CurrencyOption[], currentCurrency?: string) => {
    if (!currentCurrency) return options
    if (options.some((item) => item.code === currentCurrency)) return options
    return [{ code: currentCurrency, label: currentCurrency }, ...options]
  }

  const visibleCurrencyOptions = ensureCurrentCurrencyOption(currencyOptions, product.currency)
  const productForm = (
    <Form form={form} layout="vertical" onFinish={handleSubmit}>
      <Form.Item
        name="name"
        label="產品名稱"
        rules={[{ required: true, message: '請輸入產品名稱' }]}
      >
        <Input placeholder="例如：100 API Credits" />
      </Form.Item>
      <Form.Item name="description" label="產品描述">
        <TextArea rows={4} placeholder="產品描述（可選）" />
      </Form.Item>
      <Form.Item
        name="price"
        label="價格"
        rules={[{ required: true, message: '請輸入價格' }]}
      >
        <InputNumber
          min={0.01}
          step={0.01}
          precision={2}
          style={{ width: '100%' }}
          addonAfter={
            <Form.Item name="currency" noStyle>
              <Select
                style={{ width: 80 }}
                options={visibleCurrencyOptions.map((c) => ({
                  value: c.code,
                  label: c.label,
                  disabled: c.code === 'IDR' && product.currency !== 'IDR',
                }))}
              />
            </Form.Item>
          }
        />
      </Form.Item>

      <Form.Item>
        <Button type="primary" htmlType="submit" loading={saving} style={{ marginRight: 8 }}>
          儲存
        </Button>
        <Button onClick={() => navigate('/dashboard/products')}>取消</Button>
      </Form.Item>
    </Form>
  )

  return (
    <div>
      <Title level={4} style={{ marginBottom: 24 }}>編輯產品</Title>
      <Card style={{ maxWidth: 960 }}>
        {productForm}
      </Card>
    </div>
  )
}
